import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useScrollLock } from '../../hooks/useScrollLock';
import { LogoIcon, MicrosoftIcon } from './Icons';
import { useSession } from '../contexts/SessionContext';
import { signInWithPopup, browserPopupRedirectResolver } from 'firebase/auth';
import { auth, googleProvider, microsoftProvider } from '../../firebase';

const ReAuthenticationModal: React.FC = () => {
    const { isLocked, unlockSession } = useSession();
    const [error, setError] = useState<string | null>(null);

    useScrollLock(isLocked);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (isLocked) {
            setIsMounted(true);
        } else {
            const timer = setTimeout(() => setIsMounted(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isLocked]);

    if (!isMounted && !isLocked) return null;

    const handleGoogleLogin = async () => {
        try {
            setError(null);
            await signInWithPopup(auth, googleProvider, browserPopupRedirectResolver);
            unlockSession();
        } catch (err: any) {
            console.error("Login failed", err);
            setError(`Authentication Failed: Please try again.`);
        }
    };

    const handleMicrosoftLogin = async () => {
        try {
            setError(null);
            await signInWithPopup(auth, microsoftProvider, browserPopupRedirectResolver);
            unlockSession();
        } catch (err: any) {
            console.error("Microsoft Login failed", err);
            setError(`Authentication Failed: Please try again.`);
        }
    };

    return ReactDOM.createPortal(
        <div className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-all duration-300 ${isLocked ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className="absolute inset-0 bg-slate-900 transition-opacity" />
            
            <div className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-auto overflow-hidden transition-all duration-300 transform ${isLocked ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
                <div className="p-8 text-center">
                    <div className="flex justify-center mb-6">
                        <LogoIcon className="h-16 w-16 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Session Locked
                    </h3>

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                        Your session has timed out due to inactivity. Please re-authenticate to continue working. Your unsaved data has been preserved.
                    </p>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4">
                        <button
                            onClick={handleGoogleLogin}
                            className="w-full px-4 py-3 text-sm font-semibold text-white bg-primary rounded-lg shadow-md hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300 flex items-center justify-center gap-3"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Verify with Google
                        </button>

                        <button
                            onClick={handleMicrosoftLogin}
                            className="w-full px-4 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-300 flex items-center justify-center gap-3"
                        >
                            <MicrosoftIcon className="h-5 w-5" />
                            Verify with Microsoft
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ReAuthenticationModal;
